import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { areChildLinksDisabled, NavigationFeatureKey } from '@/config/navigationFeatures';
import type { NavigationChild } from '@/hooks/useNavigation';

interface MegaMenuProps {
  trigger: React.ReactNode;
  menuKey: string;
  isOpen: boolean;
  onToggle: () => void;
  isScrolled?: boolean;
  menuItems?: NavigationChild[];
}

const MegaMenu: React.FC<MegaMenuProps> = ({ 
  trigger, 
  menuKey, 
  isOpen, 
  onToggle,
  isScrolled = false,
  menuItems = []
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('megaMenu');
  const tNav = useTranslations('Navigation');
  const disableProjectsLinks = areChildLinksDisabled(menuKey as NavigationFeatureKey);

  // Helper function to get badge styling based on badgeType
  const getBadgeClasses = (badgeType?: string) => {
    switch(badgeType?.toLowerCase()) {
      case 'new':
        return 'bg-green-500/20 text-green-300 border border-green-400/30';
      case 'popular':
        return 'bg-orange-500/20 text-orange-300 border border-orange-400/30';
      case 'trending':
        return 'bg-pink-500/20 text-pink-300 border border-pink-400/30';
      case 'platform':
        return 'bg-blue-500/20 text-blue-300 border border-blue-400/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border border-gray-400/30';
    }
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Get translated menu data based on menuKey
  const getMenuData = () => {
    let baseItemIndex = 0;
    const takeBaseItem = () => {
      if (!menuItems || menuItems.length === 0) {
        return { item: null, index: baseItemIndex };
      }
      const item = menuItems[baseItemIndex] ?? null;
      const currentIndex = baseItemIndex;
      baseItemIndex = Math.min(menuItems.length, baseItemIndex + 1);
      return { item, index: currentIndex };
    };

    try {
      const sections = [];
      const sectionCount = t.raw(`${menuKey}.sectionsCount`) as number || 3;
      
      for (let i = 0; i < sectionCount; i++) {
        const sectionKey = `section${i + 1}`;
        const itemsCount = t.raw(`${menuKey}.${sectionKey}.itemsCount`) as number || 4;
        const items = [];
        
        for (let j = 0; j < itemsCount; j++) {
          const itemKey = `${sectionKey}.item${j + 1}`;
          const translationId = `${sectionKey}.item${j + 1}`;
          const { item: baseItem, index: baseIndex } = takeBaseItem();

          const translatedLabel = t(`${menuKey}.${itemKey}.label`);
          const sourceLabel = baseItem?.label ?? translatedLabel;
          const isFirstChild = baseIndex === 0;
          const href =
            disableProjectsLinks && !isFirstChild ? undefined : baseItem?.href;

          items.push({
            id: `${sectionKey}-item-${j + 1}`,
            label: sourceLabel,
            href,
            description: t(`${menuKey}.${itemKey}.description`),
            badge: t(`${menuKey}.${itemKey}.badge`) || undefined,
            isNew: t(`${menuKey}.${itemKey}.isNew`) === 'true',
            isPopular: t(`${menuKey}.${itemKey}.isPopular`) === 'true',
            featured: t(`${menuKey}.${itemKey}.featured`) === 'true'
          });
        }
        
        sections.push({
          id: sectionKey,
          title: t(`${menuKey}.${sectionKey}.title`),
          items
        });
      }
      
      // For callToAction, we'll try to get href from menuItems if available
      let callToActionHref = '';
      if (menuItems && menuItems.length > 0) {
        // Use the first item's href as a base and modify it for the call to action
        const basePath = menuItems[0]?.href.split('/').slice(0, -1).join('/') || '';
        callToActionHref = basePath ? `${basePath}/contact` : '/contact';
      } else {
        // Fallback to default href based on menuKey
        callToActionHref = menuKey === 'industries' ? '/industries/contact' : '/contact';
      }
      
      const callToAction = {
        title: t(`${menuKey}.callToAction.title`),
        description: t(`${menuKey}.callToAction.description`),
        href: callToActionHref,
        buttonText: t(`${menuKey}.callToAction.buttonText`)
      };
      
      return { sections, callToAction };
    } catch (error) {
      // Fallback to English defaults if translation fails
      return {
        sections: [
          {
            id: "development",
            title: "Development",
            items: [
              { 
                id: "web-development", 
                label: "Web Development", 
                href: "/solutions/web-development", 
                description: "Custom websites and web applications built with modern technologies",
                isPopular: true
              },
              { 
                id: "mobile-apps", 
                label: "Mobile Applications", 
                href: "/solutions/mobile-apps", 
                description: "Native and cross-platform mobile apps for iOS and Android",
                badge: "iOS & Android"
              },
              { 
                id: "desktop-apps", 
                label: "Desktop Applications", 
                href: "/solutions/desktop-apps", 
                description: "Powerful desktop applications for Windows, macOS, and Linux"
              },
              { 
                id: "ui-ux-design", 
                label: "UI/UX Design", 
                href: "/solutions/design", 
                description: "User-centered design that converts visitors into customers",
                featured: true
              },
            ]
          },
          {
            id: "cloud-ai",
            title: "Cloud & AI",
            items: [
              { 
                id: "cloud-solutions", 
                label: "Cloud Solutions", 
                href: "/solutions/cloud", 
                description: "Scalable cloud infrastructure and migration services",
                isNew: true
              },
              { 
                id: "ai-ml", 
                label: "AI & Machine Learning", 
                href: "/solutions/ai-ml", 
                description: "Intelligent automation and data-driven insights",
                badge: "Trending"
              },
              { 
                id: "data-analytics", 
                label: "Data Analytics", 
                href: "/solutions/analytics", 
                description: "Transform your data into actionable business intelligence"
              },
              { 
                id: "automation", 
                label: "Process Automation", 
                href: "/solutions/automation", 
                description: "Streamline operations with intelligent automation"
              },
            ]
          },
          {
            id: "security-support",
            title: "Security & Support",
            items: [
              { 
                id: "cybersecurity", 
                label: "Cybersecurity", 
                href: "/solutions/cybersecurity", 
                description: "Comprehensive security solutions to protect your business",
                isPopular: true
              },
              { 
                id: "data-protection", 
                label: "Data Protection", 
                href: "/solutions/data-protection", 
                description: "GDPR compliance and data privacy solutions"
              },
              { 
                id: "maintenance", 
                label: "Maintenance & Support", 
                href: "/solutions/support", 
                description: "24/7 technical support and system maintenance"
              },
              { 
                id: "consulting", 
                label: "IT Consulting", 
                href: "/solutions/consulting", 
                description: "Strategic technology guidance for your business growth"
              },
            ]
          }
        ],
        callToAction: {
          title: "Ready to Transform Your Business?",
          description: "Get a free consultation with our experts and discover how we can help accelerate your digital transformation.",
          href: "/contact",
          buttonText: "Get Free Consultation"
        }
      };
    }
  };

  const menuData = getMenuData();
  const hasCTA = Boolean(
    menuData?.callToAction &&
    menuData.callToAction.buttonText
  );

  const sectionCount: number = Array.isArray(menuData?.sections)
    ? menuData.sections.length
    : 0;

  // Tailwind-safe grid column classes (no dynamic numbers in class name)
  const gridColsClass = (cols: number) => {
    switch (cols) {
      case 1:
        return 'lg:grid-cols-1';
      case 2:
        return 'lg:grid-cols-2';
      case 3:
        return 'lg:grid-cols-3';
      default:
        return 'lg:grid-cols-4';
    }
  };

  const totalCols = Math.min(4, sectionCount + (hasCTA ? 1 : 0));

  // Use raw menu data without search filtering
  const effectiveMenuData = menuData;

  const [isClosing, setIsClosing] = useState(false);

  const handleClick = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsClosing(false);
    onToggle();
    setTimeout(() => setIsVisible(true), 10);
  };

  const handleClickOutside = (event: Event) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsClosing(true);
      setIsVisible(false);
      setTimeout(() => {
        onToggle();
        setIsClosing(false);
      }, 300);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  // Hide mega menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsClosing(true);
        setIsVisible(false);
        setTimeout(() => {
          onToggle();
          setIsClosing(false);
        }, 300);
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, onToggle]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsVisible(false);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      setIsClosing(false);
    } else {
      setIsClosing(false);
    }
  }, [isOpen]);

  // Reset visibility when menu key changes
  useEffect(() => {
    setIsVisible(false);
    setIsClosing(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [menuKey]);

  if (!isMounted || !menuData) return null;

  return (
    <div>
      {/* Trigger */}
      <div
        onClick={handleClick}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      {/* Mega Menu Dropdown - Portal to parent container */}
      {isOpen && createPortal(
        <div data-mega-menu>
          {/* Animated Backdrop - Only below navbar */}
          <div 
            className={`fixed ${isScrolled ? 'top-[70px]' : 'top-[85px]'} left-0 right-0 bottom-0 bg-black/20 z-[49]`}
            style={{
              animation: isClosing
                ? 'fadeOut 0.3s ease-out forwards'
                : isVisible
                  ? 'fadeIn 0.3s ease-out forwards'
                  : 'none'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClickOutside(e as any);
            }}
          />
          
          {/* Main Dropdown */}
          <div
            className={`
              fixed ${isScrolled ? 'top-[70px]' : 'top-[85px]'} left-0 right-0 w-full
              mega-menu-glass-enhanced shadow-2xl
              backdrop-blur-xl
              z-50
            `}
            ref={menuRef}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: isClosing 
                ? 'megaMenuSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                : isVisible 
                  ? 'megaMenuSlideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                  : 'none',
              opacity: isClosing ? 1 : (isVisible ? 1 : 0)
            }}
          >
            <div className="p-8 relative">
              {/* Enhanced glass overlay for better visual effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
              {/* Subtle glass reflection effect */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
              
              {/* Resources search removed */}

              <div className={`grid grid-cols-1 ${gridColsClass(totalCols)} gap-8 relative z-10`}>
                {/* Sections rendered as individual columns */}
                {effectiveMenuData.sections.map((section: any, sectionIndex: number) => (
                  <div 
                    key={section.id || sectionIndex} 
                    className="space-y-4"
                    style={{
                      opacity: isVisible ? 1 : 0,
                      animation: isVisible 
                        ? `megaMenuSectionFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${sectionIndex * 0.08}s both`
                        : 'none'
                    }}
                  >
                    <h3 className="text-lg font-semibold text-white border-b border-white/20 pb-2 bg-gradient-to-r from-white/10 to-white/5 px-3 py-2 rounded-t-lg backdrop-blur-sm">
                      {section.title}
                    </h3>
                    <div className="space-y-3">
                      {section.items.map((item: any, itemIndex: number) => {
                        const isClickable = !disableProjectsLinks && item.href;
                        const sharedClass = `
                          group flex items-start space-x-3 p-3 rounded-lg 
                          ${isClickable ? 'transition-all duration-300 ease-out' : ''}
                          ${item.featured ? 'bg-gradient-to-r from-white/20 to-white/15 shadow-lg shadow-white/10' : ''}
                          ${isClickable ? 'hover:scale-105 cursor-pointer hover:bg-white/10' : 'cursor-default pointer-events-none'}
                        `;

                        const content = (
                          <>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h4 className={`font-medium ${
                                  isClickable 
                                    ? 'text-white group-hover:text-blue-100 transition-colors duration-200' 
                                    : 'text-white'
                                }`}>
                                  {item.label}
                                </h4>
                                {item.badge && (
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    getBadgeClasses(item.badgeType)
                                  }`}>
                                    {item.badge}
                                  </span>
                                )}
                                {item.isNew && (
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-500/20 text-green-300 border border-green-400/30">
                                    {tNav('badgeLabels.new')}
                                  </span>
                                )}
                                {item.isPopular && (
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-500/20 text-orange-300 border border-orange-400/30">
                                    {tNav('badgeLabels.popular')}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className={`text-sm mt-1 ${
                                  isClickable 
                                    ? 'text-gray-300 group-hover:text-gray-200 transition-colors duration-200' 
                                    : 'text-gray-300'
                                }`}>
                                  {item.description}
                                </p>
                              )}
                            </div>
                            {isClickable && (
                              <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 group-hover:text-blue-300 transition-all duration-200 transform group-hover:translate-x-1" />
                            )}
                          </>
                        );

                        const animationStyle = {
                          opacity: isVisible ? 1 : 0,
                          animation: isVisible
                            ? `megaMenuItemFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${(sectionIndex * 0.08) + (itemIndex * 0.04) + 0.2}s both`
                            : 'none'
                        };

                        if (isClickable) {
                          return (
                            <Link
                              key={item.id || itemIndex}
                              href={item.href}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (typeof window !== 'undefined' && item.href) {
                                  window.dispatchEvent(new CustomEvent('navigation-start', {
                                    detail: { href: item.href }
                                  }));
                                }
                              }}
                              className={sharedClass}
                              style={animationStyle}
                            >
                              {content}
                            </Link>
                          );
                        }

                        return (
                          <div
                            key={item.id || itemIndex}
                            className={sharedClass}
                            style={animationStyle}
                          >
                            {content}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Call to Action Sidebar - last column when present */}
                {hasCTA && (
                  <div
                    style={{
                      opacity: isVisible ? 1 : 0,
                      animation: isVisible
                        ? `megaMenuSectionFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${effectiveMenuData.sections.length * 0.08 + 0.15}s both`
                        : 'none'
                    }}
                  >
                    <div className="relative p-6 rounded-xl border border-white/20 overflow-hidden bg-gradient-to-br from-white/10 via-white/5 to-white/10 backdrop-blur-sm">
                      {/* Enhanced Glass Background Pattern */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/8 via-transparent to-white/5" />
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/8 rounded-full -translate-y-16 translate-x-16 backdrop-blur-sm" />
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/8 rounded-full translate-y-12 -translate-x-12 backdrop-blur-sm" />
                      {/* Glass reflection line */}
                      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                      
                      {/* Content */}
                      <div className="relative z-10">
                        <h3 className="text-xl font-bold text-white mb-3">
                          {menuData.callToAction.title}
                        </h3>
                        <p className="text-gray-200 mb-6 leading-relaxed">
                          {menuData.callToAction.description}
                        </p>
                        <Link
                          href={menuData.callToAction.href}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Dispatch navigation start event
                            if (typeof window !== 'undefined') {
                              window.dispatchEvent(new CustomEvent('navigation-start', {
                                detail: { href: menuData.callToAction.href }
                              }));
                            }
                          }}
                          className="
                            inline-flex items-center justify-center w-full px-6 py-3 
                            bg-white/20 text-white font-medium 
                            rounded-lg transition-all duration-300 ease-out
                            backdrop-blur-sm
                            border border-white/30 cursor-pointer
                          "
                        >
                          {menuData.callToAction.buttonText}
                          <ChevronRight className="ml-2 w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Mobile Mega Menu Component
interface MobileMegaMenuProps {
  menuKey: string;
  isOpen: boolean;
  onClose: () => void;
}

const MobileMegaMenu: React.FC<MobileMegaMenuProps> = ({ 
  menuKey, 
  isOpen, 
  onClose 
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const t = useTranslations('megaMenu');
  const tNav = useTranslations('Navigation');
  const disableProjectsLinks = areChildLinksDisabled(menuKey as NavigationFeatureKey);

  // Helper function to get badge styling based on badgeType
  const getBadgeClasses = (badgeType?: string) => {
    switch(badgeType?.toLowerCase()) {
      case 'new':
        return 'bg-green-500/20 text-green-300';
      case 'popular':
        return 'bg-orange-500/20 text-orange-300';
      case 'trending':
        return 'bg-pink-500/20 text-pink-300';
      case 'platform':
        return 'bg-blue-500/20 text-blue-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  // Get translated menu data based on menuKey
  const getMenuData = () => {
    try {
      const sections = [];
      const sectionCount = t.raw(`${menuKey}.sectionsCount`) as number || 3;
      
      for (let i = 0; i < sectionCount; i++) {
        const sectionKey = `section${i + 1}`;
        const itemsCount = t.raw(`${menuKey}.${sectionKey}.itemsCount`) as number || 4;
        const items = [];
        
        for (let j = 0; j < itemsCount; j++) {
          const itemKey = `${sectionKey}.item${j + 1}`;
          items.push({
            id: `${sectionKey}-item-${j + 1}`,
            label: t(`${menuKey}.${itemKey}.label`),
            href: t(`${menuKey}.${itemKey}.href`),
            description: t(`${menuKey}.${itemKey}.description`),
            badge: t(`${menuKey}.${itemKey}.badge`) || undefined,
            isNew: t(`${menuKey}.${itemKey}.isNew`) === 'true',
            isPopular: t(`${menuKey}.${itemKey}.isPopular`) === 'true',
            featured: t(`${menuKey}.${itemKey}.featured`) === 'true'
          });
        }
        
        sections.push({
          id: sectionKey,
          title: t(`${menuKey}.${sectionKey}.title`),
          items
        });
      }
      
      // For callToAction, we'll use a default href based on menuKey
      let callToActionHref = '';
      if (menuKey === 'solutions') {
        callToActionHref = '/contact';
      } else if (menuKey === 'industries') {
        callToActionHref = '/industries/contact';
      } else {
        callToActionHref = '/contact';
      }
      
      const callToAction = {
        title: t(`${menuKey}.callToAction.title`),
        description: t(`${menuKey}.callToAction.description`),
        href: callToActionHref,
        buttonText: t(`${menuKey}.callToAction.buttonText`)
      };
      
      return { sections, callToAction };
    } catch (error) {
      // Fallback to English defaults if translation fails
      return {
        sections: [
          {
            id: "development",
            title: "Development",
            items: [
              { 
                id: "web-development", 
                label: "Web Development", 
                href: "/solutions/web-development", 
                description: "Custom websites and web applications built with modern technologies",
                isPopular: true
              },
              { 
                id: "mobile-apps", 
                label: "Mobile Applications", 
                href: "/solutions/mobile-apps", 
                description: "Native and cross-platform mobile apps for iOS and Android",
                badge: "iOS & Android"
              },
              { 
                id: "desktop-apps", 
                label: "Desktop Applications", 
                href: "/solutions/desktop-apps", 
                description: "Powerful desktop applications for Windows, macOS, and Linux"
              },
              { 
                id: "ui-ux-design", 
                label: "UI/UX Design", 
                href: "/solutions/design", 
                description: "User-centered design that converts visitors into customers",
                featured: true
              },
            ]
          },
          {
            id: "cloud-ai",
            title: "Cloud & AI",
            items: [
              { 
                id: "cloud-solutions", 
                label: "Cloud Solutions", 
                href: "/solutions/cloud", 
                description: "Scalable cloud infrastructure and migration services",
                isNew: true
              },
              { 
                id: "ai-ml", 
                label: "AI & Machine Learning", 
                href: "/solutions/ai-ml", 
                description: "Intelligent automation and data-driven insights",
                badge: "Trending"
              },
              { 
                id: "data-analytics", 
                label: "Data Analytics", 
                href: "/solutions/analytics", 
                description: "Transform your data into actionable business intelligence"
              },
              { 
                id: "automation", 
                label: "Process Automation", 
                href: "/solutions/automation", 
                description: "Streamline operations with intelligent automation"
              },
            ]
          },
          {
            id: "security-support",
            title: "Security & Support",
            items: [
              { 
                id: "cybersecurity", 
                label: "Cybersecurity", 
                href: "/solutions/cybersecurity", 
                description: "Comprehensive security solutions to protect your business",
                isPopular: true
              },
              { 
                id: "data-protection", 
                label: "Data Protection", 
                href: "/solutions/data-protection", 
                description: "GDPR compliance and data privacy solutions"
              },
              { 
                id: "maintenance", 
                label: "Maintenance & Support", 
                href: "/solutions/support", 
                description: "24/7 technical support and system maintenance"
              },
              { 
                id: "consulting", 
                label: "IT Consulting", 
                href: "/solutions/consulting", 
                description: "Strategic technology guidance for your business growth"
              },
            ]
          }
        ],
        callToAction: {
          title: "Ready to Transform Your Business?",
          description: "Get consultation with our experts and discover how we can help accelerate your digital transformation.",
          href: "/contact",
          buttonText: "Get Consultation"
        }
      };
    }
  };

  const menuData = getMenuData();

  const toggleSection = (sectionTitle: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionTitle)) {
      newExpanded.delete(sectionTitle);
    } else {
      newExpanded.add(sectionTitle);
    }
    setExpandedSections(newExpanded);
  };

  if (!isOpen || !menuData) return null;

  return (
    <div className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-xl z-50" onClick={onClose}>
      <div 
        className="absolute top-[70px] left-0 right-0 bottom-0 bg-black/40 backdrop-blur-2xl border-t border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          {/* Call to Action - Mobile First */}
          {menuData.callToAction && menuData.callToAction.href && menuData.callToAction.buttonText && (
            <div className="p-4 rounded-xl border border-white/20 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20">
              <h3 className="text-lg font-bold text-white mb-2">
                {menuData.callToAction.title}
              </h3>
              <p className="text-gray-200 mb-4 text-sm">
                {menuData.callToAction.description}
              </p>
              <Link
                href={menuData.callToAction.href}
                onClick={(e) => {
                  // Dispatch navigation start event
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('navigation-start', {
                      detail: { href: menuData.callToAction.href }
                    }));
                  }
                  onClose();
                }}
                className="
                  inline-flex items-center justify-center w-full px-4 py-2 
                  bg-white/20 text-white font-medium 
                  rounded-lg transition-all duration-200 backdrop-blur-sm
                  border border-white/30 cursor-pointer
                "
              >
                {menuData.callToAction.buttonText}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Sections */}
          {menuData.sections.map((section: any) => (
            <div key={section.id} className="border-b border-white/10 pb-4">
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center justify-between py-3 text-white font-semibold transition-colors"
              >
                {section.title}
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${
                  expandedSections.has(section.title) ? 'rotate-180' : ''
                }`} />
              </button>
              
              {expandedSections.has(section.title) && (
                <div className="space-y-2 pl-4">
                  {section.items.map((item: any) => {
                    const isClickable = !disableProjectsLinks && item.href;
                    const content = (
                      <>
                        <div className="flex items-center space-x-2">
                          <span>{item.label}</span>
                          {item.badge && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              getBadgeClasses(item.badgeType)
                            }`}>
                              {item.badge}
                            </span>
                          )}
                          {item.isNew && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-500/20 text-green-300">
                              {tNav('badgeLabels.new')}
                            </span>
                          )}
                          {item.isPopular && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-500/20 text-orange-300">
                              {tNav('badgeLabels.popular')}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-400 mt-1">
                            {item.description}
                          </p>
                        )}
                      </>
                    );

                    if (isClickable) {
                      return (
                        <Link
                          key={item.id}
                          href={item.href}
                          className="block py-2 text-gray-300 hover:text-white transition-colors cursor-pointer"
                          onClick={(e) => {
                            // Dispatch navigation start event
                            if (typeof window !== 'undefined') {
                              window.dispatchEvent(new CustomEvent('navigation-start', {
                                detail: { href: item.href }
                              }));
                            }
                            onClose();
                          }}
                        >
                          {content}
                        </Link>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className="block py-2 text-gray-300 cursor-default pointer-events-none"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { MegaMenu, MobileMegaMenu };